import { useState, useEffect } from 'react'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { useToast } from '../../components/ui/useToast'
import type { User } from '../../types'
import {
  Settings, Shield, Database, Mail, Key, Server,
  Bell, Globe, Lock, Save, RefreshCw, AlertCircle,
  CheckCircle, Info
} from 'lucide-react'

export function AdminSettingsPage() {
  const [user, setUser] = useState<User | null>(null)
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)

  // Platform Settings
  const [platformName, setPlatformName] = useState('StudioManager 360')
  const [supportEmail, setSupportEmail] = useState('support@studiomgr360.com')
  const [maxTenantsPerUser, setMaxTenantsPerUser] = useState('5')
  const [allowSelfRegistration, setAllowSelfRegistration] = useState(true)

  // Security Settings
  const [sessionTimeout, setSessionTimeout] = useState('30')
  const [passwordMinLength, setPasswordMinLength] = useState('8')
  const [requireMFA, setRequireMFA] = useState(false)
  const [maxLoginAttempts, setMaxLoginAttempts] = useState('5')

  // Email Settings
  const [smtpHost, setSmtpHost] = useState('')
  const [smtpPort, setSmtpPort] = useState('587')
  const [smtpUser, setSmtpUser] = useState('')
  const [emailFrom, setEmailFrom] = useState('noreply@studiomgr360.com')

  // Notification Settings
  const [notifyNewTenant, setNotifyNewTenant] = useState(true)
  const [notifyNewUser, setNotifyNewUser] = useState(true)
  const [notifyFailedPayment, setNotifyFailedPayment] = useState(true)

  useEffect(() => {
    ;(async () => {
      try {
        const sessionRes = await fetch('/api/admin/session', { credentials: 'include' })
        if (sessionRes.ok) {
          const sessionData = await sessionRes.json()
          if (sessionData.success && sessionData.data?.user) {
            setUser(sessionData.data.user)
          }
        }
      } catch {
        // Silent fail
      }
    })()
  }, [])

  const handleSaveGeneral = async () => {
    setSaving(true)
    setTimeout(() => {
      setSaving(false)
      notify({ kind: 'success', message: 'General settings saved successfully' })
    }, 1000)
  }

  const handleSaveSecurity = async () => {
    setSaving(true)
    setTimeout(() => {
      setSaving(false)
      notify({ kind: 'success', message: 'Security settings updated' })
    }, 1000)
  }

  const handleSaveEmail = async () => {
    setSaving(true)
    setTimeout(() => {
      setSaving(false)
      notify({ kind: 'success', message: 'Email settings configured' })
    }, 1000)
  }

  const handleSaveNotifications = async () => {
    setSaving(true)
    setTimeout(() => {
      setSaving(false)
      notify({ kind: 'success', message: 'Notification preferences saved' })
    }, 1000)
  }

  const SettingSection = ({
    icon: Icon,
    title,
    description,
    children
  }: {
    icon: any
    title: string
    description: string
    children: React.ReactNode
  }) => (
    <div className="card">
      <div className="card-header flex items-center gap-3">
        <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-500/10">
          <Icon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg">{title}</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
        </div>
      </div>
      <div className="card-body space-y-4">
        {children}
      </div>
    </div>
  )

  const ToggleSwitch = ({
    checked,
    onChange,
    label
  }: {
    checked: boolean
    onChange: (checked: boolean) => void
    label: string
  }) => (
    <label className="flex items-center justify-between cursor-pointer group">
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked
            ? 'bg-orange-600'
            : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </label>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
          Platform Settings
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2 flex items-center gap-2">
          <Settings className="h-4 w-4" />
          Configure global platform settings and preferences
        </p>
      </div>

      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-800 rounded-xl">
        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
        <div className="flex-1 text-sm">
          <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">Configuration Note</h3>
          <p className="text-blue-800 dark:text-blue-200">
            These settings control platform-wide behavior. Changes will affect all tenants and users.
            Settings are currently stored in browser state and will be persisted to database in production.
          </p>
        </div>
      </div>

      {/* General Settings */}
      <SettingSection
        icon={Globe}
        title="General Settings"
        description="Basic platform configuration and branding"
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Platform Name</label>
            <Input
              value={platformName}
              onChange={(e) => setPlatformName(e.target.value)}
              placeholder="Platform name"
            />
          </div>
          <div>
            <label className="form-label">Support Email</label>
            <Input
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
              type="email"
              placeholder="support@example.com"
            />
          </div>
          <div>
            <label className="form-label">Max Tenants per User</label>
            <Input
              value={maxTenantsPerUser}
              onChange={(e) => setMaxTenantsPerUser(e.target.value)}
              type="number"
              min="1"
              max="100"
            />
          </div>
        </div>

        <div className="pt-2">
          <ToggleSwitch
            checked={allowSelfRegistration}
            onChange={setAllowSelfRegistration}
            label="Allow Self-Registration"
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSaveGeneral}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </SettingSection>

      {/* Security Settings */}
      <SettingSection
        icon={Shield}
        title="Security & Authentication"
        description="Password policies and session management"
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Session Timeout (minutes)</label>
            <Input
              value={sessionTimeout}
              onChange={(e) => setSessionTimeout(e.target.value)}
              type="number"
              min="5"
              max="1440"
            />
          </div>
          <div>
            <label className="form-label">Password Min Length</label>
            <Input
              value={passwordMinLength}
              onChange={(e) => setPasswordMinLength(e.target.value)}
              type="number"
              min="6"
              max="32"
            />
          </div>
          <div>
            <label className="form-label">Max Login Attempts</label>
            <Input
              value={maxLoginAttempts}
              onChange={(e) => setMaxLoginAttempts(e.target.value)}
              type="number"
              min="3"
              max="10"
            />
          </div>
        </div>

        <div className="pt-2">
          <ToggleSwitch
            checked={requireMFA}
            onChange={setRequireMFA}
            label="Require Multi-Factor Authentication"
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSaveSecurity}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <Lock className="h-4 w-4" />
            {saving ? 'Saving...' : 'Update Security'}
          </Button>
        </div>
      </SettingSection>

      {/* Email Settings */}
      <SettingSection
        icon={Mail}
        title="Email Configuration"
        description="SMTP settings for transactional emails"
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="form-label">SMTP Host</label>
            <Input
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              placeholder="smtp.example.com"
            />
          </div>
          <div>
            <label className="form-label">SMTP Port</label>
            <Input
              value={smtpPort}
              onChange={(e) => setSmtpPort(e.target.value)}
              type="number"
              placeholder="587"
            />
          </div>
          <div>
            <label className="form-label">SMTP Username</label>
            <Input
              value={smtpUser}
              onChange={(e) => setSmtpUser(e.target.value)}
              placeholder="username"
            />
          </div>
          <div>
            <label className="form-label">From Email</label>
            <Input
              value={emailFrom}
              onChange={(e) => setEmailFrom(e.target.value)}
              type="email"
              placeholder="noreply@example.com"
            />
          </div>
        </div>

        <div className="flex items-start gap-3 p-3 bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            SMTP credentials are stored securely. For production, use environment variables.
          </p>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={handleSaveEmail}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <Mail className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Email Config'}
          </Button>
        </div>
      </SettingSection>

      {/* Notification Settings */}
      <SettingSection
        icon={Bell}
        title="Admin Notifications"
        description="Configure when to receive platform notifications"
      >
        <div className="space-y-3">
          <ToggleSwitch
            checked={notifyNewTenant}
            onChange={setNotifyNewTenant}
            label="Notify on new tenant registration"
          />
          <ToggleSwitch
            checked={notifyNewUser}
            onChange={setNotifyNewUser}
            label="Notify on new user signups"
          />
          <ToggleSwitch
            checked={notifyFailedPayment}
            onChange={setNotifyFailedPayment}
            label="Notify on failed payments"
          />
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSaveNotifications}
            disabled={saving}
            className="flex items-center gap-2"
          >
            <Bell className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Preferences'}
          </Button>
        </div>
      </SettingSection>

      {/* Database & System Info */}
      <SettingSection
        icon={Database}
        title="System Information"
        description="Platform status and database details"
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Server className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Runtime</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Cloudflare Workers</p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Database className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Database</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Cloudflare D1 (SQLite)</p>
          </div>
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Key className="h-4 w-4 text-gray-600 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">API Version</span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">v1.0.0</p>
          </div>
          <div className="p-4 bg-green-50 dark:bg-green-500/10 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-700 dark:text-green-300">Status</span>
            </div>
            <p className="text-sm text-green-600 dark:text-green-400">All Systems Operational</p>
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button variant="outline" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Clear Cache
          </Button>
          <Button variant="outline" className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            Run Migrations
          </Button>
        </div>
      </SettingSection>
    </div>
  )
}
